import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export interface ProjectMember {
  id: string;
  project_id: string;
  user_email: string;
  user_name: string | null;
  role: 'lead' | 'member';
  created_at: string;
}

export function useProjectMembers(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-members', projectId],
    queryFn: async () => {
      if (!projectId) return [];
      const response = await api.projectMembers.list(projectId);
      return response.members as ProjectMember[];
    },
    enabled: !!projectId,
  });
}

export function useAddProjectMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, data }: {
      projectId: string;
      data: { user_email: string; user_name?: string; role?: string };
    }) => api.projectMembers.add(projectId, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project-members', variables.projectId] });
      toast.success('Team member added');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add team member');
    },
  });
}

export function useRemoveProjectMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, userEmail }: { projectId: string; userEmail: string }) =>
      api.projectMembers.remove(projectId, userEmail),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project-members', variables.projectId] });
      toast.success('Team member removed');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to remove team member');
    },
  });
}
